import { useEffect, useMemo, useState } from "react";
import { addDoc, collection, doc, getDoc, increment, onSnapshot, query, serverTimestamp, updateDoc, where, writeBatch } from "firebase/firestore";
import { MP_AMBIENTE, mercadoPagoEmUso, mpHabilitado } from "../utils/mercadoPago";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { C } from "../constants/theme";
import { Btn, Card, Inp } from "./ui";
import { toast } from "../hooks/useToast";
import { useConfirm } from "../hooks/useConfirm";
import { fbAdd, fbList, fbCollectionPath } from "../hooks/useFirebase";
import { alertasLoja, centavos, DIAS_PARADA, limiteAviso, precoDaLoja, rankingLoja, repassePorDesconto, resumoLoja } from "../utils/consignacao";
import { ListaVendas, MarcaParceria, Numero, useLojaParceira } from "./PainelLojaParceira";
import { ToolButton, ToolDialog, ToolEmpty, ToolSearch } from "./ToolWorkspace";
import { HeaderIcon } from "./AppHeader";
import { CalcIcon } from "./calculadora/CalcIcon";
import "./siteWorkspace.css";
import "./lojasParceiras.css";

/* Aba "Lojas parceiras" (consignação), lado do dono: cadastra a loja pelo
   e-mail, leva peças com repasse fixo e preço máximo, acompanha vendas,
   alertas e quanto a loja deve. O acerto é combinado entre os dois; aqui só
   se registra o repasse recebido. */

const convidar = dados => httpsCallable(getFunctions(undefined, "us-central1"), "convidarLojaParceira")(dados);
const brl = v => "R$ " + centavos(v).toFixed(2).replace(".", ",");
const num = v => parseFloat(String(v ?? "").replace(",", ".")) || 0;
const inteiro = v => Math.max(0, parseInt(v, 10) || 0);
const campo = { background: C.panel, border: "1px solid " + C.border, borderRadius: 8, color: C.text, padding: "6px 8px", width: 80 };
const fotoDe = p => p.fotos?.find(f => f.tipo !== "video")?.url || "";
// Foto que vai pra loja: o link do Storage; a miniatura em base64 só se for pequena (limite do documento).
const fotoParaLoja = p => fotoDe(p) || (String(p.fotoBase64 || "").length < 150000 ? p.fotoBase64 || "" : "");
const estoqueDe = p => Math.max(0, parseInt(p.estoque, 10) || 0);

// Escolher o que levar, com as fotos do catálogo (como em Site → Adicionar à
// vitrine). Com estoque e sem estoque ficam separados; a quantidade de quem
// tem estoque vai até o que você tem em casa.
function EscolherProdutos({ loja, produtos, itens, onLevar, onClose }) {
  const [busca, setBusca] = useState("");
  const [escolha, setEscolha] = useState({});
  const [enviando, setEnviando] = useState(false);
  const naLoja = Object.fromEntries(itens.map(i => [i.id, i]));
  const filtrados = produtos.filter(p => (p.nome || "").toLowerCase().includes(busca.trim().toLowerCase()));
  const comEstoque = filtrados.filter(p => estoqueDe(p) > 0), semEstoque = filtrados.filter(p => !estoqueDe(p));
  const mudar = (p, delta) => setEscolha(e => {
    const qtd = Math.min(estoqueDe(p) || Infinity, (e[p.id] || 0) + delta);
    const { [p.id]: _, ...resto } = e;
    return qtd > 0 ? { ...resto, [p.id]: qtd } : resto;
  });
  const produtosEscolhidos = Object.keys(escolha).length, pecas = Object.values(escolha).reduce((t, q) => t + q, 0);
  const levar = async () => { setEnviando(true); await onLevar(escolha); setEnviando(false); };

  const cartao = p => {
    const preco = centavos(num(p.preco)), qtd = escolha[p.id] || 0, foto = fotoDe(p) || p.fotoBase64, item = naLoja[p.id];
    return (
      <article key={p.id} className="site-product" style={qtd ? { borderColor: C.accent2, boxShadow: "0 0 0 1px " + C.accent2 } : undefined}>
        <div className="site-product-photo">{foto ? <img src={foto} alt={p.nome} loading="lazy" /> : <HeaderIcon name="cube" size={42} />}
          {item?.quantidade > 0 && <span className="site-live">{item.quantidade} já na loja</span>}</div>
        <div className="site-product-content">
          <h3>{p.nome}</h3>
          <div className="site-product-price"><strong>{brl(preco)}</strong><span>{estoqueDe(p) ? estoqueDe(p) + " em estoque" : "Sem estoque"}</span></div>
          <p style={{ fontSize: 12, color: C.muted, margin: "8px 0 0" }}>{preco > 0 ? "Seu repasse: " + brl(item?.repasse ?? repassePorDesconto(preco, loja.descontoPadrao)) : "Sem preço: cadastre em Produtos para levar."}</p>
          {preco > 0 && <div className="tool-actions">{qtd ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ToolButton secondary aria-label={"Uma peça a menos de " + p.nome} onClick={() => mudar(p, -1)}>−</ToolButton>
              <strong aria-live="polite" style={{ minWidth: 24, textAlign: "center" }}>{qtd}</strong>
              <ToolButton secondary aria-label={"Uma peça a mais de " + p.nome} disabled={estoqueDe(p) > 0 && qtd >= estoqueDe(p)} onClick={() => mudar(p, 1)}>+</ToolButton>
            </div>
          ) : <ToolButton secondary onClick={() => mudar(p, 1)}>Selecionar</ToolButton>}</div>}
        </div>
      </article>
    );
  };
  const secao = (titulo, lista, dica) => lista.length > 0 && (
    <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 14, color: C.text }}>{titulo} <span style={{ color: C.muted, fontWeight: 400 }}>({lista.length})</span></h3>
      {dica && <p style={{ margin: 0, fontSize: 12, color: C.muted }}>{dica}</p>}
      <div className="site-products">{lista.map(cartao)}</div>
    </section>
  );

  return (
    <ToolDialog title={"Levar produtos para " + loja.nome} subtitle="Toque nos produtos, escolha a quantidade e envie tudo de uma vez." icon="materiais" color="#71e5ce" onClose={onClose} busy={enviando}>
      <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <ToolSearch value={busca} onChange={setBusca} placeholder="Buscar produto" />
        {!filtrados.length && <ToolEmpty icon="materiais" title="Nenhum produto encontrado">Cadastre produtos em Produtos ou ajuste a busca.</ToolEmpty>}
        {secao("Com estoque", comEstoque)}
        {secao("Sem estoque", semEstoque, "Também dá para levar: a peça vai para a loja e o seu estoque continua em zero.")}
        <div style={{ position: "sticky", bottom: 0, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between", padding: "12px 0", background: C.panel, borderTop: "1px solid " + C.border }}>
          <span style={{ color: C.muted, fontSize: 13 }}>{produtosEscolhidos ? produtosEscolhidos + " produto(s) · " + pecas + " peça(s)" : "Nenhum produto selecionado"}</span>
          <ToolButton disabled={!produtosEscolhidos || enviando} onClick={levar}>{enviando ? "Enviando…" : "Levar para a loja"}</ToolButton>
        </div>
      </div>
    </ToolDialog>
  );
}

function NovaLoja({ onCriada, onClose }) {
  const [f, setF] = useState({ nome: "", email: "", descontoPadrao: "15" });
  const [enviando, setEnviando] = useState(false);
  const criar = async e => {
    e.preventDefault();
    setEnviando(true);
    try {
      const { data } = await convidar({ nome: f.nome, email: f.email, descontoPadrao: num(f.descontoPadrao) });
      toast("Loja cadastrada. Enviamos o convite para o e-mail dela.");
      setF({ nome: "", email: "", descontoPadrao: f.descontoPadrao });
      onCriada(data.lojaId);
    } catch (err) { toast(err?.message || "Não foi possível cadastrar a loja.", "error"); }
    setEnviando(false);
  };
  return (
    <ToolDialog title="Adicionar loja parceira" subtitle="Cadastre a parceria e envie o convite de acesso." icon="clientes" compact busy={enviando} onClose={onClose}>
      <form onSubmit={criar} className="tool-module consignacao-create">
        <fieldset disabled={enviando} className="consignacao-fields">
          <Inp label="Nome da loja" required value={f.nome} onChange={e => setF({ ...f, nome: e.target.value })} />
          <Inp label="E-mail da loja" type="email" required value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
          <Inp label="Desconto da loja (%)" inputMode="decimal" value={f.descontoPadrao} onChange={e => setF({ ...f, descontoPadrao: e.target.value })} />
        </fieldset>
        <p className="consignacao-note">A loja recebe um convite por e-mail e cria a senha em "Criar conta → Sou loja parceira". Ela escolhe o preço de venda até o máximo que você definir; o desconto é o lucro dela.</p>
        <div className="tool-actions tool-actions--end"><ToolButton secondary disabled={enviando} onClick={onClose}>Cancelar</ToolButton><ToolButton type="submit" disabled={enviando} icon="clientes">{enviando ? "Enviando convite…" : "Adicionar loja"}</ToolButton></div>
      </form>
    </ToolDialog>
  );
}

function CartaoLoja({ loja, onAbrir }) {
  const dados = useLojaParceira(loja.id);
  const r = resumoLoja(dados);
  const { parados, baixos } = alertasLoja(dados);
  return (
    <button type="button" className="consignacao-store" onClick={onAbrir} aria-label={`Abrir parceria com ${loja.nome}`}>
      <span className="consignacao-store-heading"><span className="site-store-logo">{loja.logoLoja ? <img src={loja.logoLoja} alt="" /> : <HeaderIcon name="clientes" size={25} />}</span><span className="consignacao-store-name"><strong>{loja.nome}</strong><small>{loja.email}</small></span><CalcIcon name="chevron" /></span>
      <span className="consignacao-store-balance"><small>A receber</small><strong style={{ color: r.saldo > 0 ? C.warning : C.text }}>{brl(r.saldo)}</strong></span>
      <span className="consignacao-store-stats"><span><small>Na loja</small><strong>{r.pecas} peças</strong></span><span><small>Vendido</small><strong>{brl(r.vendido)}</strong></span></span>
      {(parados.length > 0 || baixos.length > 0) && <span className="consignacao-store-alert">{[baixos.length && `${baixos.length} para repor`, parados.length && `${parados.length} parada(s)`].filter(Boolean).join(" · ")}</span>}
      <span className="consignacao-store-open">Ver parceria <HeaderIcon name="arrow" size={16} /></span>
    </button>
  );
}

function DetalheLoja({ loja, produtos, onVoltar, onProdutosMudaram }) {
  const { itens, vendas, repasses, avisos } = useLojaParceira(loja.id);
  const [confirmar, modalConfirm] = useConfirm();
  const [qtds, setQtds] = useState({});
  const [edits, setEdits] = useState({});
  const [selecionados, setSelecionados] = useState(new Set());
  const [pct, setPct] = useState(String(loja.descontoPadrao ?? 15));
  const [escolhendo, setEscolhendo] = useState(false);
  const [repasse, setRepasse] = useState({ valor: "", obs: "" });
  const r = resumoLoja({ itens, vendas, repasses });
  const { parados, baixos } = alertasLoja({ itens, vendas });
  const ranking = rankingLoja(vendas).slice(0, 10);
  const ordenados = useMemo(() => [...itens].sort((a, b) => (a.nome || "").localeCompare(b.nome || "")), [itens]);
  const itemRef = id => doc(db, "lojas_parceiras", loja.id, "itens", id);
  const produtoRef = id => doc(db, fbCollectionPath("produtos"), id);

  // Levar peças: entram na loja e saem do seu estoque; devolver faz o contrário.
  const mover = async (id, delta, novo) => {
    const produto = produtos.find(p => p.id === id);
    const batch = writeBatch(db);
    if (novo) batch.set(itemRef(id), novo);
    else batch.update(itemRef(id), { quantidade: increment(delta) });
    if (produto) batch.update(produtoRef(id), { estoque: Math.max(0, (parseInt(produto.estoque, 10) || 0) - delta) });
    try { await batch.commit(); onProdutosMudaram(); setQtds(q => ({ ...q, [id]: "" })); }
    catch { toast("Não foi possível atualizar o estoque.", "error"); }
  };
  // Vários de uma vez (seletor com fotos): peça nova entra com o repasse do desconto da loja.
  const levarVarios = async escolha => {
    const batch = writeBatch(db);
    for (const [id, qtd] of Object.entries(escolha)) {
      const p = produtos.find(x => x.id === id);
      if (!p) continue;
      const precoMaximo = centavos(num(p.preco));
      if (itens.some(i => i.id === id)) batch.update(itemRef(id), { quantidade: increment(qtd) });
      else batch.set(itemRef(id), { nome: p.nome || "", foto: fotoParaLoja(p), quantidade: qtd, vendidos: 0, precoMaximo, precoVenda: precoMaximo, repasse: repassePorDesconto(precoMaximo, loja.descontoPadrao), enviadoEm: serverTimestamp() });
      batch.update(produtoRef(id), { estoque: Math.max(0, estoqueDe(p) - qtd) });
    }
    try {
      await batch.commit();
      onProdutosMudaram();
      setEscolhendo(false);
      toast("Produtos levados para " + loja.nome + ".");
    } catch { toast("Não foi possível levar os produtos.", "error"); }
  };
  const levarMais = (item, qtd) => qtd > 0 && mover(item.id, qtd);
  const devolver = item => {
    const qtd = Math.min(inteiro(qtds[item.id]) || 1, Number(item.quantidade) || 0);
    if (qtd > 0) mover(item.id, -qtd);
  };

  const salvarValor = async (item, chave) => {
    const valor = centavos(num(edits[item.id + chave]));
    setEdits(e => ({ ...e, [item.id + chave]: undefined }));
    if (!(valor > 0) || valor === item[chave]) return;
    const patch = { [chave]: valor };
    if (chave === "precoMaximo" && Number(item.precoVenda) > valor) patch.precoVenda = valor;
    try { await updateDoc(itemRef(item.id), patch); } catch { toast("Não foi possível salvar.", "error"); }
  };
  const salvarAviso = async item => {
    const chave = item.id + "avisarCom";
    const valor = inteiro(edits[chave]);
    setEdits(e => ({ ...e, [chave]: undefined }));
    if (valor === limiteAviso(item)) return;
    try { await updateDoc(itemRef(item.id), { avisarCom: valor }); } catch { toast("Não foi possível salvar o aviso.", "error"); }
  };
  const aplicarDesconto = async () => {
    const alvo = itens.filter(i => !selecionados.size || selecionados.has(i.id));
    if (!alvo.length) return;
    const batch = writeBatch(db);
    alvo.forEach(i => batch.update(itemRef(i.id), { repasse: repassePorDesconto(i.precoMaximo, num(pct)) }));
    try { await batch.commit(); toast(`Repasse de ${alvo.length} produto(s) = ${num(pct)}% abaixo do preço máximo.`); setSelecionados(new Set()); }
    catch { toast("Não foi possível aplicar o desconto.", "error"); }
  };
  const registrarRepasse = async e => {
    e.preventDefault();
    const valor = centavos(num(repasse.valor || r.saldo));
    if (!(valor > 0)) return;
    try {
      await addDoc(collection(db, "lojas_parceiras", loja.id, "repasses"), { valor, obs: repasse.obs.trim(), criadoEm: serverTimestamp() });
      await fbAdd("caixa", { descricao: `Repasse da loja parceira ${loja.nome}`, valor, tipo: "Entrada", data: new Date().toISOString() });
      setRepasse({ valor: "", obs: "" });
      toast("Repasse registrado e lançado no Financeiro.");
    } catch { toast("Não foi possível registrar o repasse.", "error"); }
  };
  // Pix normal avisado pela loja: você confere no banco e dá a baixa (ou recusa)
  const pendentes = avisos.filter(a => a.status === "pendente");
  const resolverAviso = async (aviso, recebido) => {
    const batch = writeBatch(db);
    const avisoRef = doc(db, "lojas_parceiras", loja.id, "avisos", aviso.id);
    batch.update(avisoRef, { status: recebido ? "confirmado" : "recusado", resolvidoEm: serverTimestamp() });
    if (recebido) batch.set(doc(db, "lojas_parceiras", loja.id, "repasses", "aviso_" + aviso.id), { valor: Number(aviso.valor), obs: "Pix avisado pela loja", avisoId: aviso.id, criadoEm: serverTimestamp() });
    try {
      await batch.commit();
      if (recebido) await fbAdd("caixa", { descricao: `Repasse da loja parceira ${loja.nome}`, valor: Number(aviso.valor), tipo: "Entrada", data: new Date().toISOString(), formaPagamento: "pix" });
      toast(recebido ? "Repasse confirmado e lançado no Financeiro." : "Aviso recusado. A loja vê que o Pix não foi encontrado.");
    } catch { toast("Não foi possível atualizar o aviso.", "error"); }
  };

  // Excluir: as peças que estão lá voltam pro seu estoque e o histórico é apagado.
  const excluir = async () => {
    const aviso = [
      `Excluir ${loja.nome}? A loja perde o acesso e as vendas e repasses dela são apagados.`,
      r.pecas > 0 && `As ${r.pecas} peça(s) que estão lá voltam para o seu estoque.`,
      r.saldo > 0 && `Atenção: a loja ainda deve ${brl(r.saldo)}.`,
    ].filter(Boolean).join(" ");
    if (!await confirmar(aviso)) return;
    const base = ["lojas_parceiras", loja.id];
    const ops = [];
    for (const i of itens) {
      const p = produtos.find(x => x.id === i.id);
      if (p && Number(i.quantidade) > 0) ops.push(b => b.update(produtoRef(i.id), { estoque: (parseInt(p.estoque, 10) || 0) + Number(i.quantidade) }));
      ops.push(b => b.delete(itemRef(i.id)));
    }
    vendas.forEach(v => ops.push(b => b.delete(doc(db, ...base, "vendas", v.id))));
    repasses.forEach(p => ops.push(b => b.delete(doc(db, ...base, "repasses", p.id))));
    avisos.forEach(a => ops.push(b => b.delete(doc(db, ...base, "avisos", a.id))));
    ops.push(b => b.delete(doc(db, ...base)));
    try {
      // Lotes de até 450 escritas; a loja sai no último, depois do histórico.
      for (let n = 0; n < ops.length; n += 450) {
        const batch = writeBatch(db);
        ops.slice(n, n + 450).forEach(op => op(batch));
        await batch.commit();
      }
      toast("Loja excluída.");
      onProdutosMudaram();
      onVoltar();
    } catch { toast("Não foi possível excluir a loja.", "error"); }
  };
  const reenviar = async () => {
    try { await convidar({ lojaId: loja.id }); toast("Convite reenviado."); }
    catch (e) { toast(e?.message || "Não foi possível reenviar.", "error"); }
  };
  const editavel = (item, chave) => (
    <input inputMode="decimal" aria-label={`${chave === "repasse" ? "Repasse" : "Preço máximo"} de ${item.nome}`} style={campo}
      value={edits[item.id + chave] ?? Number(item[chave] || 0).toFixed(2).replace(".", ",")}
      onChange={e => setEdits(x => ({ ...x, [item.id + chave]: e.target.value }))}
      onBlur={() => edits[item.id + chave] !== undefined && salvarValor(item, chave)} />
  );
  const titulo = t => <h3 style={{ color: C.text, fontSize: 15, margin: "8px 0" }}>{t}</h3>;

  return (
    <div className="tool-module consignacao-workspace consignacao-detail">
      {modalConfirm}
      {escolhendo && <EscolherProdutos loja={loja} produtos={produtos} itens={itens} onLevar={levarVarios} onClose={() => setEscolhendo(false)} />}
      <div className="consignacao-detail-heading">
        <div>
          <ToolButton secondary onClick={onVoltar}><CalcIcon name="back" />Todas as lojas</ToolButton>
          <div style={{ marginTop: 12 }}><MarcaParceria loja={loja} subtitulo={loja.email} /></div>
        </div>
        <div className="tool-actions">
          <ToolButton secondary onClick={reenviar}>Reenviar convite</ToolButton>
          <ToolButton danger onClick={excluir}><CalcIcon name="trash" />Excluir loja</ToolButton>
        </div>
      </div>

      <div className="consignacao-numbers">
        <Numero titulo="Vendido pela loja" valor={brl(r.vendido)} />
        <Numero titulo="Seu repasse" valor={brl(r.repasse)} dica={`lucro da loja ${brl(r.lucroLoja)}`} />
        <Numero titulo="Já recebido" valor={brl(r.recebido)} />
        <Numero titulo="A receber" valor={brl(r.saldo)} cor={r.saldo > 0 ? C.warning : C.text} />
        <Numero titulo="Na loja" valor={`${r.pecas} peças`} dica={`${brl(r.valorNaLoja)} em repasse`} />
      </div>

      {pendentes.length > 0 && <Card>
        <div className="consignacao-section-heading"><h3>💸 Pix avisados pela loja</h3><p>Confira no seu banco se o Pix caiu e dê a baixa. Quem paga pelo Mercado Pago já tem baixa automática.</p></div>
        {pendentes.map(a => (
          <div key={a.id} style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderTop: "1px solid " + C.border }}>
            <span style={{ color: C.text, fontSize: 13 }}><strong>{brl(a.valor)}</strong> <span style={{ color: C.muted }}>· avisado em {a.criadoEm?.toDate?.().toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) || "agora"}</span></span>
            <span className="tool-actions">
              <ToolButton onClick={() => resolverAviso(a, true)}>Recebi, dar baixa</ToolButton>
              <ToolButton secondary onClick={() => resolverAviso(a, false)}>Não recebi</ToolButton>
            </span>
          </div>
        ))}
      </Card>}

      <Card>
        <div className="consignacao-section-heading"><h3>Registrar acerto</h3><p>Recebeu da loja? Registre o valor para atualizar o saldo da parceria.</p></div>
        <form onSubmit={registrarRepasse} style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
          <div style={{ flex: "0 1 140px" }}><Inp label="Repasse recebido (R$)" inputMode="decimal" placeholder={centavos(r.saldo).toFixed(2).replace(".", ",")} value={repasse.valor} onChange={e => setRepasse({ ...repasse, valor: e.target.value })} /></div>
          <div style={{ flex: "1 1 180px" }}><Inp label="Observação" placeholder="Ex.: Pix do acerto de outubro" value={repasse.obs} onChange={e => setRepasse({ ...repasse, obs: e.target.value })} /></div>
          <Btn color={C.success}>Registrar repasse</Btn>
        </form>
      </Card>

      {(baixos.length > 0 || parados.length > 0) && <Card>
        {titulo("⚠️ Alertas")}
        {baixos.map(i => (
          <div key={"b" + i.id} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", fontSize: 13, padding: "4px 0", color: C.text }}>
            <span style={{ flex: 1 }}>📉 <strong>{i.nome}</strong>: {i.quantidade > 0 ? `resta ${i.quantidade}` : "esgotou"}. Sugestão: levar {i.sugestao} (vendeu {i.sugestao} em 30 dias).</span>
            <Btn small color={C.accent2} onClick={() => levarMais(i, i.sugestao)}>Levar {i.sugestao}</Btn>
          </div>
        ))}
        {parados.map(i => (
          <div key={"p" + i.id} style={{ fontSize: 13, padding: "4px 0", color: C.text }}>💤 <strong>{i.nome}</strong>: {i.quantidade} sem vender há {i.dias} dias (mais de {DIAS_PARADA}). Vale trocar por outra peça.</div>
        ))}
      </Card>}

      <Card>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", justifyContent: "space-between" }}>
          {titulo("Produtos na loja")}
          <ToolButton icon="cube" onClick={() => setEscolhendo(true)}>Levar produtos</ToolButton>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10, fontSize: 13, color: C.muted }}>
          Repasse = <input inputMode="decimal" aria-label="Desconto em %" style={{ ...campo, width: 60 }} value={pct} onChange={e => setPct(e.target.value)} />% abaixo do preço máximo
          <Btn small color={C.accent2} onClick={aplicarDesconto} disabled={!itens.length}>{selecionados.size ? `Aplicar nos ${selecionados.size} marcados` : "Aplicar em todos"}</Btn>
        </div>
        <p style={{ color: C.muted, fontSize: 12, margin: "0 0 10px" }}>📦 Você recebe um aviso no celular quando uma peça acaba na loja. Para avisar antes, marque em "Avisar com" quantas devem sobrar (ex.: chaveiro com 1).</p>
        {!itens.length && <p style={{ color: C.muted, fontSize: 13 }}>Nenhuma peça na loja ainda. Toque em "+ Levar produtos" para escolher pelo catálogo.</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ordenados.map(i => (
            <div className="consignacao-item" key={i.id} style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: 10, background: C.panel, borderRadius: 10, fontSize: 12, color: C.muted }}>
              <input type="checkbox" aria-label={`Marcar ${i.nome}`} checked={selecionados.has(i.id)} onChange={() => setSelecionados(s => { const n = new Set(s); n.has(i.id) ? n.delete(i.id) : n.add(i.id); return n; })} />
              <span className="consignacao-item-photo">{i.foto ? <img src={i.foto} alt="" /> : <HeaderIcon name="cube" size={24} />}</span>
              <div style={{ flex: "1 1 140px", minWidth: 0 }}><strong style={{ color: C.text, fontSize: 13 }}>{i.nome}</strong><div>{i.quantidade} na loja · {i.vendidos || 0} vendidos · loja vende a {brl(precoDaLoja(i))}</div></div>
              <label>Repasse<br />{editavel(i, "repasse")}</label>
              <label>Preço máximo<br />{editavel(i, "precoMaximo")}</label>
              <label title="Avisa no seu celular quando restar essa quantidade na loja (0 = quando acabar)">Avisar com<br /><input inputMode="numeric" aria-label={`Avisar quando restarem quantas peças de ${i.nome}`} style={{ ...campo, width: 50 }}
                value={edits[i.id + "avisarCom"] ?? String(limiteAviso(i))} onChange={e => setEdits(x => ({ ...x, [i.id + "avisarCom"]: e.target.value }))}
                onBlur={() => edits[i.id + "avisarCom"] !== undefined && salvarAviso(i)} /></label>
              <label>Qtd<br /><input inputMode="numeric" aria-label={`Quantidade de ${i.nome}`} style={{ ...campo, width: 50 }} placeholder="1" value={qtds[i.id] ?? ""} onChange={e => setQtds(q => ({ ...q, [i.id]: e.target.value }))} /></label>
              <Btn small color={C.accent2} onClick={() => levarMais(i, inteiro(qtds[i.id]) || 1)}>+ Levar</Btn>
              <Btn small outline onClick={() => devolver(i)} disabled={!i.quantidade}>− Devolver</Btn>
            </div>
          ))}
        </div>
      </Card>

      {ranking.length > 0 && <Card>
        {titulo("🏆 Mais vendidos nesta loja")}
        {ranking.map((x, n) => <div key={x.id} style={{ fontSize: 13, color: C.text, padding: "3px 0" }}>{n + 1}. {x.nome} — {x.qtd} un. · {brl(x.valor)}</div>)}
      </Card>}

      <Card>{titulo("Vendas")}<ListaVendas vendas={vendas} /></Card>
    </div>
  );
}

export default function LojasParceiras({ uid, perfil }) {
  const [lojas, setLojas] = useState([]);
  const [aberta, setAberta] = useState(null);
  const [nova, setNova] = useState(false);
  const [produtos, setProdutos] = useState([]);
  const carregarProdutos = () => fbList("produtos").then(setProdutos).catch(() => {});

  useEffect(() => {
    if (!uid) return;
    return onSnapshot(query(collection(db, "lojas_parceiras"), where("donoUid", "==", uid)),
      s => setLojas(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.nome || "").localeCompare(b.nome || ""))),
      e => console.error("lojas parceiras", e));
  }, [uid]);
  useEffect(() => { carregarProdutos(); }, []);
  // A loja não lê users/{dono}: o nome e a logo do dono vão copiados no documento dela.
  const donoNome = perfil?.empresa || perfil?.nome || "";
  const donoLogo = String(perfil?.logoUrl || "").length < 200000 ? perfil?.logoUrl || "" : "";
  // Pix do dono (para o repasse da loja) e se o Mercado Pago dele está em uso
  // (aí o repasse vai pelo Pix do Mercado Pago, com baixa automática).
  const pcJson = JSON.stringify(perfil?.paymentConfig || {});
  const pc = useMemo(() => JSON.parse(pcJson), [pcJson]);
  const donoPix = useMemo(() => pc.pixChave ? { chave: String(pc.pixChave), nome: String(pc.pixBeneficiario || donoNome).slice(0, 25), cidade: String(pc.pixCidade || "").slice(0, 15) } : null, [pc, donoNome]);
  const [donoMp, setDonoMp] = useState(null);
  useEffect(() => {
    if (!uid || !mpHabilitado()) return; // fora do site o app não sabe do Mercado Pago: não mexe
    getDoc(doc(db, "users", uid, "mercadopago", MP_AMBIENTE)).then(s => setDonoMp(mercadoPagoEmUso(pc, s.data()))).catch(() => {});
  }, [uid, pc]);
  useEffect(() => {
    if (!perfil) return;
    const igual = (a, b) => JSON.stringify(a || null) === JSON.stringify(b || null);
    lojas.filter(l => (l.donoLogo || "") !== donoLogo || (donoNome && l.donoNome !== donoNome) || !igual(l.donoPix, donoPix) || (donoMp !== null && !!l.donoMp !== donoMp))
      .forEach(l => updateDoc(doc(db, "lojas_parceiras", l.id), { donoLogo, donoPix, ...(donoNome && { donoNome }), ...(donoMp !== null && { donoMp }) }).catch(() => {}));
  }, [lojas, perfil, donoNome, donoLogo, donoPix, donoMp]);

  const loja = lojas.find(l => l.id === aberta);
  if (loja) return <DetalheLoja key={loja.id} loja={loja} produtos={produtos} onVoltar={() => setAberta(null)} onProdutosMudaram={carregarProdutos} />;

  return (
    <div className="tool-module consignacao-workspace">
      <section className="site-hero consignacao-hero">
        <div className="site-hero-copy"><span className="tool-eyebrow">SEU ESTÚDIO / PARCERIAS</span><h1>Suas peças em <span>mais lugares.</span></h1><p>Deixe peças em consignação. A loja vende, fica com o lucro dela e você acompanha o estoque e os acertos por aqui.</p></div>
        <div className="consignacao-hero-action"><span className="site-store-logo"><HeaderIcon name="clientes" size={28} /></span><div><strong>Lojas parceiras</strong><p>{lojas.length ? `${lojas.length} ${lojas.length === 1 ? "parceria cadastrada" : "parcerias cadastradas"}` : "Sua próxima parceria começa aqui"}</p></div><ToolButton icon="clientes" onClick={() => setNova(true)}>Adicionar loja</ToolButton></div>
      </section>
      {nova && <NovaLoja onClose={() => setNova(false)} onCriada={id => { setNova(false); setAberta(id); }} />}
      <div className="consignacao-list-heading"><h2>Suas lojas parceiras</h2><span>{lojas.length} {lojas.length === 1 ? "loja" : "lojas"}</span></div>
      {!lojas.length ? <ToolEmpty icon="clientes" title="Amplie sua rede de vendas">Clique em "Adicionar loja" para cadastrar a primeira parceria e enviar o convite de acesso.</ToolEmpty> : <div className="consignacao-stores">{lojas.map(l => <CartaoLoja key={l.id} loja={l} onAbrir={() => setAberta(l.id)} />)}</div>}
    </div>
  );
}
